<template>
  <div
    id="advanced_settings_container"
    class="bg-transparent text_color rounded-lg proxima_nova"
  >
    <div @click="toggleExpanded" class="flex items-center cursor-pointer gap-2">
      <div class="secondary_medium rounded-lg px-2">
        <ThemeImage
          v-if="!expanded"
          :icon="imageNames.BOTTOM_ARROW"
          alt="down_arrow"
          class="size-4"
        />
        <ThemeImage
          v-else
          :icon="imageNames.UP_ARROW"
          alt="up_arrow"
          class="size-4"
        />
      </div>
      <span class="text-[16px] font-semibold">Advanced Settings</span>
    </div>

    <div v-if="expanded" class="mt-6 flex flex-col gap-6">
      <div v-for="(setting, index) in settings" :key="index">
        <div class="flex items-center justify-between">
          <div>
            <span class="text-sm text_color font-semibold">
              {{ setting.title }}
            </span>
            <p class="secondary_color text-xs">{{ setting.description }}</p>
          </div>
          <a-switch
            v-model:checked="setting.enabled"
            size="small"
            @change="() => handleSettingChange(index)"
          />
        </div>

        <div v-if="setting.enabled && index === 0" class="mt-4">
          <div class="flex items-center gap-4">
            <a-radio-group v-model:value="limit.type">
              <div class="flex flex-row gap-2">
                <a-radio value="fixed" class="text_color"> </a-radio>
                <div class="flex items-center gap-2">
                  <CustomInput
                    format="string"
                    v-model="limit.days"
                    type="primary"
                    class="w-20"
                  />
                  <a-select
                    v-model:value="limit.days_type"
                    class="w-40"
                    :options="timeUnitOptions"
                  >
                    <template #suffixIcon>
                      <ThemeImage
                        :icon="imageNames.BOTTOM_ARROW"
                        alt="bottom_white_arrow"
                        class="size-4"
                      />
                    </template>
                  </a-select>
                  <span class="text-[13px] whitespace-nowrap">into the future</span>
                </div>
              </div>
              <div class="flex flex-row gap-2 mt-4">
                <a-radio value="date_range" class="text_color"></a-radio>
                <div class="flex flex-row items-center gap-2">
                  <span class="text-[13px]">Within a certain period</span>
                  <div class="flex gap-4 cursor-pointer">
                    <a-date-picker
                      :value="limit.start_date"
                      format="YYYY-MM-DD"
                      class="w-full"
                      placeholder="Start date"
                      :disabled-date="disablePastDates"
                      @change="handleStartDateChange"
                    />
                    <a-date-picker
                      :value="limit.end_date"
                      format="YYYY-MM-DD"
                      class="w-full"
                      placeholder="End date"
                      :disabled-date="
                        (current) => disableEndDates(current, limit.start_date)
                      "
                      @change="handleEndDateChange"
                    />
                  </div>
                </div>
              </div>
            </a-radio-group>
          </div>
        </div>

        <div v-if="setting.enabled && index === 1" class="mt-4">
          <div class="flex flex-row gap-4 w-full">
            <div class="flex-1">
              <span class="text-sm secondary_color">Before event</span>
              <a-select
                v-model:value="buffer_time.before"
                class="w-full"
                :options="bufferTimeOptions"
              >
                <template #suffixIcon>
                  <ThemeImage
                    :icon="imageNames.BOTTOM_ARROW"
                    alt="bottom_white_arrow"
                    class="size-4"
                  />
                </template>
              </a-select>
            </div>
            <div class="flex-1">
              <span class="text-sm secondary_color">After event</span>
              <a-select
                v-model:value="buffer_time.after"
                class="w-full"
                :options="bufferTimeOptions"
              >
                <template #suffixIcon>
                  <ThemeImage
                    :icon="imageNames.BOTTOM_ARROW"
                    alt="bottom_white_arrow"
                    class="size-4"
                  />
                </template>
              </a-select>
            </div>
          </div>
        </div>

        <div v-if="setting.enabled && index === 2" class="mt-4">
          <div class="flex flex-row gap-4 w-full">
            <div class="flex-1">
              <CustomInput
                format="text"
                v-model="notice.duration"
                placeholder="Enter value"
                type="primary"
                class="w-full"
              />
            </div>
            <div class="flex-1">
              <a-select
                v-model:value="notice.duration_type"
                class="w-full"
                :options="timeOptionsConst"
              >
                <template #suffixIcon>
                  <ThemeImage
                    :icon="imageNames.BOTTOM_ARROW"
                    alt="bottom_white_arrow"
                    class="size-4"
                  />
                </template>
              </a-select>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, watch, nextTick } from "vue";
import {
  Switch as ASwitch,
  DatePicker as ADatePicker,
  Radio as ARadio,
  RadioGroup as ARadioGroup,
  Select as ASelect,
} from "ant-design-vue";
import dayjs from "dayjs";
import _ from "lodash";
import CustomInput from "@/src/components/CustomInput.vue";

import {
  bufferTimeOptions,
  timeOptionsConst,
  timeUnitOptions,
} from "@/src/constants/meetingAgentConstants.js";

import { imageNames } from "@/src/constants/imageConstants.js";

const expanded = ref(false);
const emit = defineEmits(["update:advancedSettings"]);

const props = defineProps({
  initialSettings: {
    type: Object,
    default: () => null,
  },
});

const limit = reactive({
  type: "fixed",
  days: "45",
  days_type: "business",
  start_date: null,
  end_date: null,
});

const buffer_time = reactive({
  before: 15,
  after: 5,
});

const notice = reactive({
  duration: "2",
  duration_type: "hours",
});

const settings = reactive([
  {
    title: "Limit future bookings",
    description: "Limit how far in the future this event can be booked",
    enabled: false,
  },
  {
    title: "Buffer time",
    description: "Add time before and after booked meeting",
    enabled: false,
  },
  {
    title: "Minimum notice",
    description: "Set the minimum amount of notice that is required",
    enabled: false,
  },
]);

const toggleExpanded = () => {
  expanded.value = !expanded.value;

  if (expanded.value) {
    nextTick(() => {
      const container = document.getElementById("advanced_settings_container");
      if (container) {
        container.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    });
  }
};

const handleSettingChange = (index) => {
  if (!settings[index].enabled) {
    if (index === 0) {
      limit.type = "fixed";
      limit.days = "45";
      limit.days_type = "business";
      limit.start_date = null;
      limit.end_date = null;
    } else if (index === 1) {
      buffer_time.before = 15;
      buffer_time.after = 5;
    } else if (index === 2) {
      notice.duration = "2";
      notice.duration_type = "hours";
    }
    debouncedEmit();
  }
};

const handleStartDateChange = (date) => {
  nextTick(() => {
    limit.start_date = date;
  });
};

const handleEndDateChange = (date) => {
  nextTick(() => {
    limit.end_date = date;
  });
};

const disablePastDates = (current) => {
  return current && current < dayjs().startOf("day");
};

const disableEndDates = (current, startDate) => {
  if (!startDate) {
    return disablePastDates(current);
  }
  return (
    current && (current < dayjs().startOf("day") || current < dayjs(startDate))
  );
};

const emitSettings = () => {
  let limitDaysConfig = null;
  if (settings[0].enabled) {
    if (limit.type === "fixed") {
      limitDaysConfig = {
        type: "fixed",
        days: limit.days.toString(),
        days_type: limit.days_type,
      };
    } else if (limit.type === "date_range") {
      limitDaysConfig = {
        type: "date_range",
        start_date: limit.start_date
          ? dayjs(limit.start_date).format("YYYY-MM-DD")
          : null,
        end_date: limit.end_date
          ? dayjs(limit.end_date).format("YYYY-MM-DD")
          : null,
      };
    }
  }

  const advancedSettings = {
    limit_days_enabled: settings[0].enabled,
    limit_days_config: limitDaysConfig,
    buffer_time_enabled: settings[1].enabled,
    buffer_time_config: settings[1].enabled
      ? {
          before: buffer_time.before,
          after: buffer_time.after,
        }
      : null,
    minimum_notice_enabled: settings[2].enabled,
    minimum_notice_config: settings[2].enabled
      ? {
          duration: notice.duration.toString(),
          duration_type: notice.duration_type,
        }
      : null,
  };

  emit("update:advancedSettings", advancedSettings);
};

const debouncedEmit = _.debounce(() => {
  emitSettings();
}, 300);

watch(
  [settings, buffer_time, notice],
  () => {
    debouncedEmit();
  },
  { deep: true },
);

watch(
  () => ({ ...limit }),
  () => {
    debouncedEmit();
  },
  { deep: true },
);

watch(
  () => limit.type,
  (newType) => {
    if (newType === "fixed") {
      limit.start_date = null;
      limit.end_date = null;
    }
  },
);

watch(
  () => props.initialSettings,
  (newSettings) => {
    settings.forEach((setting) => (setting.enabled = false));
    limit.type = "fixed";
    limit.days = "45";
    limit.days_type = "business";
    limit.start_date = null;
    limit.end_date = null;
    buffer_time.before = 15;
    buffer_time.after = 5;
    notice.duration = "2";
    notice.duration_type = "hours";
    if (!newSettings) return;
    const hasEnabledSettings =
      newSettings.limit_days_enabled ||
      newSettings.buffer_time_enabled ||
      newSettings.minimum_notice_enabled;

    expanded.value = hasEnabledSettings;

    if (newSettings.limit_days_enabled) {
      settings[0].enabled = true;
      if (newSettings.limit_days_config) {
        const config = newSettings.limit_days_config;
        limit.type = config.type;
        if (config.type === "fixed") {
          limit.days = config.days;
          limit.days_type = config.days_type;
          limit.start_date = null;
          limit.end_date = null;
        } else if (config.type === "date_range") {
          limit.start_date = config.start_date
            ? dayjs(config.start_date)
            : null;
          limit.end_date = config.end_date ? dayjs(config.end_date) : null;
        }
      }
    }

    if (newSettings.buffer_time_enabled) {
      settings[1].enabled = true;
      if (newSettings.buffer_time_config) {
        buffer_time.before = newSettings.buffer_time_config.before;
        buffer_time.after = newSettings.buffer_time_config.after;
      }
    }

    if (newSettings.minimum_notice_enabled) {
      settings[2].enabled = true;
      if (newSettings.minimum_notice_config) {
        notice.duration = newSettings.minimum_notice_config.duration;
        notice.duration_type = newSettings.minimum_notice_config.duration_type;
      }
    }
  },
  { immediate: true, deep: true },
);
</script>

<style scoped>
:deep(.ant-radio-wrapper) {
  color: white !important;
}

:deep(.ant-select-selection-placeholder) {
  color: rgba(255, 255, 255, 0.25) !important;
}
</style>
